import React, { useState, useEffect } from 'react'
import classNames from 'classnames'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import { ParentMenus as DesktopParentMenus } from '@toasttab/shared/src/components/organisms/Navigation/Desktop/ParentMenus'
import { ParentMenus as MobileParentMenus } from '@toasttab/shared/src/components/organisms/Navigation/Mobile/ParentMenus'
import MainNavigationContext from '@toasttab/shared/src/data/HeaderNavigation/context/MainNavigationContext'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import { determinePhoneAvailability } from '@toasttab/shared/src/components/organisms/Navigation/PhoneNumberLink'
import { MainNavigationProps } from '@toasttab/shared/src/data/HeaderNavigation/MainNavigationMenu'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import withNinetailedExperience from '@toasttab/shared/src/utils/Ninetailed/withNinetailedExperience'
import TopNavigationHeaderButton from '@toasttab/shared/src/components/organisms/Navigation/Ninetailed/NT_TopNavigationHeaderButton'

const MainNavigation = ({
  mobileSalesIndicatorSchedule,
  navigationData
}: MainNavigationProps) => {
  const [selectedParentMenu, setSelectedParentMenu] = useState(null)
  const [mobileNavOpen, setMobileNavOpen] = useState(null)
  const [isAvailable, setIsAvailable] = useState(false)

  const pageContext = usePageContext()
  const { siteRegion } = navigationData

  const globalSettings = pageContext?.globalSettings
  const headerStyle = pageContext?.headerStyle || 'Default'
  const showMainNavContent = headerStyle === 'Default'
  const showLogin = ['Default', 'Logo & Login'].includes(headerStyle)
  const showSales = siteRegion === 'us'

  const navHeapClass =
    siteRegion === 'us' ? `topnav_entire_v` : `${siteRegion}_topnav_entire`
  const rxHeapClass = `${siteRegion}_topnav_restaurants`
  const demoHeapClass = `${siteRegion}_topnav_demo`
  const loginHeapClass = `${siteRegion}_topnav_login`

  const navCss = classNames(
    'flex flex-nowrap z-[100] bg-white content-center items-end justify-between w-full h-[56px] min-[992px]:h-[72px] font-sansPro pt-6',
    {
      'border-b border-b-transparent min-[992px]:border-gray-50':
        headerStyle !== 'Logo Only'
    }
  )
  const baseHamburgerCss =
    'block absolute h-0.5 w-5 bg-gray-125 transform transition duration-500 ease-in-out'
  const hamburgerTopCss = classNames(baseHamburgerCss, {
    'rotate-45': mobileNavOpen,
    '-translate-y-1.5': !mobileNavOpen
  })
  const hamburgerMidCss = classNames(baseHamburgerCss, {
    'opacity-0': mobileNavOpen
  })
  const hamburgerBottomCss = classNames(baseHamburgerCss, {
    '-rotate-45': mobileNavOpen,
    'translate-y-1.5': !mobileNavOpen
  })

  const loginCss = classNames({
    'font-sansPro text-base font-semibold text-blue-75 mr-6':
      pageContext?.styleContext !== 'request-demo',
    'appearance-none leading-none inline-flex items-center relative transition-all duration-300 box-border w-full px-0 undefined !h-12 justify-center text-center text-link bg-white rounded-md border-2 border-blue-75 hover:border-blue-100 hover:text-link-hover hover:border-blue-100 font-sansPro cursor-pointer py-[14px] px-8 md:w-auto text-base font-semibold':
      pageContext?.styleContext === 'request-demo'
  })

  const toggleMobileMenu = () => {
    setMobileNavOpen(!mobileNavOpen)
    setSelectedParentMenu(null)
  }

  useEffect(() => {
    setIsAvailable(determinePhoneAvailability(mobileSalesIndicatorSchedule))

    const handleMobileEsc = (event: any) => {
      if (event.key === 'Escape' || event.key == 'ESC' || event.keyCode == 27) {
        setSelectedParentMenu(null)
        setMobileNavOpen(false)
      }
    }

    window.addEventListener('keydown', handleMobileEsc)
  }, [])

  const renderHeaderCTA = () => {
    // this is an example of how to use the Ninetailed experience
    // if (globalSettings) {
    //   return withNinetailedExperience(
    //     TopNavigationHeaderButton,
    //     globalSettings.topNavigationHeader
    //   )
    // }

    return (
      <a
        href={navigationData.demoUrl}
        className={`${demoHeapClass} appearance-none leading-none inline-flex items-center relative transition-all duration-300 box-border justify-center text-center font-sansPro leaeding-none text-white bg-blue-75 rounded-lg border-blue-75 border-2 hover:bg-blue-100 hover:border-blue-100 cursor-pointer py-3 px-6 text-base font-semibold`}
      >
        Get a Demo
      </a>
    )
  }

  return (
    <MainNavigationContext.Provider
      value={{
        selectedParentMenu,
        mobileNavOpen,
        setSelectedParentMenu,
        setMobileNavOpen
      }}
    >
      <nav
        aria-label='Toast Main Navigation'
        className={`${navHeapClass} relative z-[100]`}
      >
        <div className={navCss}>
          <DesktopParentMenus
            navigationData={navigationData}
            showDropdowns={showMainNavContent}
          />
          <div className='hidden min-[992px]:flex flex-nowrap items-center pb-[12px] mr-4 md:mr-6 min-[992px]:mr-7 min-[1440px]:mr-[156px]'>
            {showMainNavContent && siteRegion === 'us' && (
              <>
                <a
                  className={`${rxHeapClass} font-sansPro text-base font-semibold text-blue-75 before:h-6 before:w-6 before:content-icon-blue-map-marker before:mr-2 hidden min-[1440px]:block`}
                  href={navigationData.restaurantUrl}
                >
                  Restaurants
                </a>
                <span className='mx-3 font-semibold text-blue-75 hidden min-[1440px]:block'>
                  |
                </span>
              </>
            )}
            {showLogin && (
              <a
                className={`${loginHeapClass} ${loginCss}`}
                href={navigationData.loginUrl}
              >
                Login
              </a>
            )}
            {showMainNavContent && renderHeaderCTA()}
          </div>
          {showMainNavContent && (
            <div className='flex min-[992px]:hidden flex-nowrap items-center pb-[18px] mr-4 md:mr-6 min-[992px]:mr-7 '>
              {showSales && (
                <>
                  <div
                    className={`${
                      isAvailable ? 'bg-grass-25' : 'bg-gray-50'
                    } px-2 py-1 text-[0.75rem] font-normal leading-[0.875rem] rounded-[999px] mr-1`}
                  >
                    Call Sales
                  </div>
                  <a
                    href='tel:6172971005'
                    className='font-sansPro text-[0.875rem] leading-[1rem] text-blue-75 mr-4 md:mr-14'
                  >
                    (617) 297-1005
                  </a>
                </>
              )}
              <button
                className='w-5 h-5'
                onClick={toggleMobileMenu}
                aria-haspopup='true'
                aria-expanded={mobileNavOpen ? true : false}
                aria-label='Toggle Toast Mobile Navigation'
                aria-controls='mobile-nav-menu'
              >
                <span aria-hidden='true' className={hamburgerTopCss}></span>
                <span aria-hidden='true' className={hamburgerMidCss}></span>
                <span aria-hidden='true' className={hamburgerBottomCss}></span>
              </button>
            </div>
          )}
          {showLogin && !showMainNavContent && (
            <a
              className={`font-sansPro text-base leading-none ${
                pageContext?.styleContext === 'request-demo'
                  ? 'font-normal lg:font-semibold'
                  : 'font-semibold'
              } text-blue-75 self-start mr-4 md:mr-6 min-[992px]:hidden`}
              href={navigationData.loginUrl}
            >
              Login
            </a>
          )}
        </div>
        {showMainNavContent && (
          <MobileParentMenus navigationData={navigationData} />
        )}
      </nav>
    </MainNavigationContext.Provider>
  )
}

export default MainNavigation
